import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject} from '@angular/core';
import {Localization} from '../../../../projects/common/src/lib/interface/localization.extension';
import {AuthGuardService} from '../../common/services/auth-guard.service';
import {ImpersonationHandOff, ImpersonationService} from '../../common/services/impersonation.service';
import {consoleOrigin} from '../../common/console-url';
import {Logger} from '../../common/utils/logger';

type Phase = 'waiting' | 'switching' | 'failed' | 'not-from-console';

/** How long to wait for the console's hand-off before saying it did not come. */
const WAIT_MS = 45_000;

/**
 * /impersonate: where the admin console's "View as user" lands.
 *
 * The console opened this tab and holds the customer's sign-in token. This
 * page tells it, by postMessage to the console's origin only, that it is
 * ready, and takes the token from a message that came from that origin and
 * from the window that opened it, nothing else. The token never travels in
 * the URL.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-impersonate-page',
  templateUrl: 'impersonate.page.html',
})
export class ImpersonatePage extends Localization implements OnInit, OnDestroy {
  private impersonation = inject(ImpersonationService);
  private authGuard = inject(AuthGuardService);

  phase: Phase = 'waiting';
  private readonly origin = consoleOrigin();
  private pinger?: ReturnType<typeof setInterval>;
  private giveUp?: ReturnType<typeof setTimeout>;

  private readonly onMessage = (event: MessageEvent) => {
    const handOff = event.data as ImpersonationHandOff;
    if (event.origin !== this.origin || event.source !== window.opener || handOff?.type !== 'mm-impersonate'
      || !handOff.targetToken || !handOff.targetUid) {
      return;
    }
    this.stopWaiting();
    this.phase = 'switching';
    this.impersonation.startFromConsole(handOff).catch(err => {
      Logger.error('Viewing as the user failed', err);
      this.phase = 'failed';
    });
  };

  ngOnInit() {
    this.authGuard.loader.set(false);
    this.authGuard.loader$.next(false);
    if (!window.opener) {
      this.phase = 'not-from-console';
      return;
    }
    window.addEventListener('message', this.onMessage);
    // Until the console answers: it may still be minting the token.
    const ready = () => {
      try {
        window.opener?.postMessage({type: 'mm-impersonate-ready'}, this.origin);
      } catch (e) {
        // The opener is gone or another origin: nothing to tell.
      }
    };
    ready();
    this.pinger = setInterval(ready, 500);
    this.giveUp = setTimeout(() => {
      this.stopWaiting();
      if (this.phase === 'waiting') {
        this.phase = 'failed';
      }
    }, WAIT_MS);
  }

  ngOnDestroy() {
    this.stopWaiting();
  }

  closeTab() {
    window.close();
  }

  private stopWaiting() {
    window.removeEventListener('message', this.onMessage);
    clearInterval(this.pinger);
    clearTimeout(this.giveUp);
  }
}
